import {
  type CollectionMandateRules,
  type CollectionSettings,
  MANDATE_BLOCK_LEVELS,
  MANDATE_ONE_TIME_BASES,
} from "@repo/shared/types";
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useUpdateMandateSettings } from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./mandate-rules-card.i18n";

type MandateRulesCardProps = {
  portfolioCollectionId: string;
  settings: CollectionSettings;
};

/**
 * QL-375 — the enforcement rules (five since QL-677), saved on change. One set per
 * collection, governing every mandate used in it, template-derived or synced
 * from the bank. A template holds only ISINs and weights; the bank supplies
 * none of this.
 */
export function MandateRulesCard({ portfolioCollectionId, settings }: MandateRulesCardProps) {
  const t = useT(T);
  const update = useUpdateMandateSettings();
  const [error, setError] = useState<string | null>(null);

  const save = (patch: Partial<CollectionMandateRules>) => {
    setError(null);
    update.mutate(
      { id: portfolioCollectionId, data: patch },
      {
        onError: (err: Error) => setError(err.message || t.failedToSave),
        // QL-677 — switching the create-from-portfolio permission off leaves
        // existing bases running; say how many open advices keep one.
        onSuccess: (res) => {
          if (res.affectedBasisPortfolios) {
            toast.info(t.affectedBasisToast(String(res.affectedBasisPortfolios)));
          }
        },
      },
    );
  };

  // QL-664 — goal-level blocking and the buy basis are refused together by the
  // service; the conflicting option is disabled here so the save never fails.
  const goalLevelBlocked = settings.mandateOneTimeBasis === "buy";
  const buyBasisBlocked = settings.mandateBlockLevel === "goal";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div
            role="alert"
            className="text-sm text-destructive bg-destructive/10 p-3 rounded-md whitespace-pre-line"
          >
            {error}
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="mandateBlockLevel">{t.blockLevel}</Label>
            <p className="text-xs text-muted-foreground">{t.blockLevelHint}</p>
            {goalLevelBlocked && (
              <p className="text-xs text-muted-foreground">{t.blockLevelGoalDisabledHint}</p>
            )}
          </div>
          <Select
            value={settings.mandateBlockLevel}
            onValueChange={(value) =>
              save({ blockLevel: value as (typeof MANDATE_BLOCK_LEVELS)[number] })
            }
            disabled={update.isPending}
          >
            <SelectTrigger id="mandateBlockLevel" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="goal" disabled={goalLevelBlocked}>
                {t.blockLevelGoal}
              </SelectItem>
              <SelectItem value="portfolio">{t.blockLevelPortfolio}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="mandateOneTimeBasis">{t.oneTimeBasis}</Label>
            <p className="text-xs text-muted-foreground">{t.oneTimeBasisHint}</p>
            {buyBasisBlocked && (
              <p className="text-xs text-muted-foreground">{t.oneTimeBasisBuyDisabledHint}</p>
            )}
          </div>
          <Select
            value={settings.mandateOneTimeBasis}
            onValueChange={(value) =>
              save({ oneTimeBasis: value as (typeof MANDATE_ONE_TIME_BASES)[number] })
            }
            disabled={update.isPending}
          >
            <SelectTrigger id="mandateOneTimeBasis" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="final_portfolio">{t.oneTimeBasisFinal}</SelectItem>
              <SelectItem value="buy" disabled={buyBasisBlocked}>
                {t.oneTimeBasisBuy}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="mandateAllowOneTimeDeviation">{t.allowOneTimeDeviation}</Label>
            <p className="text-xs text-muted-foreground">{t.allowOneTimeDeviationHint}</p>
          </div>
          <Switch
            id="mandateAllowOneTimeDeviation"
            checked={settings.mandateAllowOneTimeDeviation}
            onCheckedChange={(checked) => save({ allowOneTimeDeviation: checked })}
            disabled={update.isPending}
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="mandateAllowRecurringDeviation">{t.allowRecurringDeviation}</Label>
            <p className="text-xs text-muted-foreground">{t.allowRecurringDeviationHint}</p>
          </div>
          <Switch
            id="mandateAllowRecurringDeviation"
            checked={settings.mandateAllowRecurringDeviation}
            onCheckedChange={(checked) => save({ allowRecurringDeviation: checked })}
            disabled={update.isPending}
          />
        </div>

        {/* QL-677 — whether advisors may make a custom portfolio the goal's mandate. */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="mandateAllowCreateFromPortfolio">{t.allowCreateFromPortfolio}</Label>
            <p className="text-xs text-muted-foreground">{t.allowCreateFromPortfolioHint}</p>
          </div>
          <Switch
            id="mandateAllowCreateFromPortfolio"
            checked={settings.mandateAllowCreateFromPortfolio}
            onCheckedChange={(checked) => save({ allowCreateFromPortfolio: checked })}
            disabled={update.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}
